import dayjs from 'dayjs';
import 'dayjs/plugin/timezone';
import { supportedLngs } from '@app/configs/i18n';
import { DayjsLocale, KZ_TIMEZONE } from '@shared/constants/dayjs';
import { Lng, LngRecord } from '@shared/types/types';

export const getInlineLangText = (
    lngContent: LngRecord | string | null | undefined,
    langs: Lng[],
): string => {
    if (!lngContent) return '';

    if (typeof lngContent === 'string') return lngContent;

    return langs
        .filter((x) => lngContent[x]?.trim())
        .map((x) => lngContent[x])
        .filter((value, index, array) => array.indexOf(value) === index)
        .join(' / ');
};

const DayjsLocalizedFormat: Partial<Record<Lng, string>> = {
    ru: 'D MMMM YYYY г.',
    kk: 'D MMMM YYYY ж.',
    en: 'MMMM D, YYYY',
};

export const getFormattedDate = (
    date: string | undefined | null,
    {
        format,
        lang,
    }:
        | { lang: Lng; format?: undefined }
        | { format: string; lang?: undefined } = {
        format: 'DD.MM.YYYY HH:mm',
    },
) => {
    try {
        if (!dayjs(date).isValid()) return date || '';
        if (!date) return '';
        if (lang) {
            return dayjs(date)
                .tz(KZ_TIMEZONE)
                .locale(DayjsLocale[lang] || 'ru')
                .format(DayjsLocalizedFormat[lang]);
        } else {
            return dayjs(date).tz(KZ_TIMEZONE).format(format);
        }
    } catch (error) {
        return date || '';
    }
};

export const getLangTextValue = (
    extraTitle?: Record<string, string> | string | null,
    lang?: string,
) => {
    if (!extraTitle) return undefined;
    if (typeof extraTitle === 'string') return extraTitle;
    if (lang) {
        if (extraTitle[lang]) {
            return extraTitle[lang];
        }
    }

    const langValue = supportedLngs.find((x) => extraTitle[x]);
    const firstValue = Object.values(extraTitle).filter((x) => x)[0];

    if (langValue) return extraTitle[langValue];
    else if (firstValue) return firstValue;

    return undefined;
};

export const getLangText = (
    extraTitle?: Record<string, string> | string | null,
    lang?: string,
    defaultValue = '',
) => {
    return getLangTextValue(extraTitle, lang)?.trim() || defaultValue;
};

export const capitalize = (word?: string) => {
    if (!word) return '';
    return word.charAt(0).toUpperCase() + word.slice(1);
};

export const uncapitalize = (word?: string) => {
    if (!word) return '';
    return word.charAt(0).toLowerCase() + word.slice(1);
};

export const getAbbreviation = (string?: string) => {
    if (!string) return '';
    const words = string.split(' ');
    if (words.length === 1) return words[0];
    return words.map((x) => x.charAt(0).toUpperCase()).join('');
};
